import { centsToDollars } from '@core/util/money';
import { Address } from '@models/address';
import { ClientType } from '@models/clients';
import { Encumbrance } from '@models/encumbrance';
import { ComplianceRequest } from '@models/jurisdictions';
import {
	ActingForType,
	Brokerage,
	BusinessMatterClientWithAssociations,
	DepositHolder,
	MatterFileType,
	MatterPropertyDetails,
	MatterTitle,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import {
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import { MatterRequisitionPrecedent } from '@models/requisitions';
import { TitleInsuranceProvider } from '@models/title-insurance';
import { Selector } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessAddressesState } from '@state/business/address';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { cloneDeep, isEqual } from 'lodash';
import { MatterClients } from '../matter_clients';
import {
	GetArePartiesSigningPowerOfAttorney,
	GetExistingUninsuredMortgages,
	GetFirm,
	GetFirmRepresentParty,
	GetLawyerDetails,
	GetMortgages,
	GetPolicyType,
	GetProperty,
	GetTransactionType,
	GetWasDepositPaidToVendor,
} from './chicago.util';
import { removeUndefinedAndNull } from './stewart.util';
import {
	SubmitData,
	PurchaserBorrowerName,
	PurchaseBorrower,
	MatterChicagoTitleInsurance,
	Firm,
} from '@models/title-insurance/chicago';
import { BusinessAddress } from '@models/businesses';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import LogRocket from 'logrocket';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';

export class ChicagoSelectors {
	@Selector([
		SelectedMatter.matterNumber,
		SelectedMatter.closingDate,
		SelectedMatter.getTitleInsuranceResolvedPurchasePrice,
		SelectedMatter.getSelectedDepositHolder,
		SelectedMatter.actingFor,
		MatterMortgagesState.getAllMortgagesWithAssociations,
		SelectedMatter.propertyDetails,
		SelectedMatter.matterFileType,
		BusinessState.businessName,
		BusinessState.chicagoFirmName,
		BusinessAddressesState.getPrimaryAddress,
		SelectedMatter.office,
		MatterClients.get_mortgagorsWithAssociationMapped,
		MatterClients.get_otherPartyWithAssociationMapped,
		SelectedMatter.clientBrokerage,
		SelectedMatter.otherPartyBrokerage,
		MatterEncumbrancesState.getAll,
		SelectedMatter.otherPartyLawFirm,
		MatterJurisdictionSelectors.getAllComplianceRequests,
		MatterRequisitionsState.getAll,
		SelectedMatter.getTitleInsuranceProvider,
		SelectedMatter.getChicagoFirm,
		MatterTitlesState.getAll,
	])
	static getChicagoDataPayload(
		matterNumber: string,
		closingDate: string,
		purchasePrice: number,
		depositHolder: DepositHolder,
		actingFor: ActingForType,
		allMortgages: MatterMortgageWithAssociations[],
		propertyDetails: MatterPropertyDetails,
		fileType: MatterFileType,
		businessName: string,
		chicagoFirmName: string,
		businessAddress: Address,
		office: BusinessAddress,
		clients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		otherParty: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		brokerage: Brokerage,
		vendorBrokerage: Brokerage,
		encumbrances: Encumbrance[],
		otherPartyLawFirm: LawFirm,
		complianceRequests: ComplianceRequest[],
		matterRequisitions: MatterRequisitionPrecedent[],
		provider: TitleInsuranceProvider,
		prevFirm: Firm,
		matterTitles: MatterTitle[],
	): SubmitData {
		try {
			if (provider !== TitleInsuranceProvider.CHICAGO) {
				return null;
			}
			const mortgages = allMortgages?.filter(
				(x) => x.transactionType !== TransactionType.BRIDGE && !x.mms,
			);

			const data: SubmitData = {
				LawyerReferenceNumber: matterNumber,
				Province: propertyDetails?.address?.province,
				TransactionType: GetTransactionType(fileType, allMortgages),
				Properties: [
					GetProperty(
						fileType,
						propertyDetails,
						brokerage,
						vendorBrokerage,
						otherParty,
						encumbrances,
						complianceRequests,
						matterRequisitions,
						mortgages,
						matterTitles,
					),
				],
				ClosingDate: closingDate,
				Firm:
					prevFirm ||
					GetFirm(chicagoFirmName, office, businessName, businessAddress),
				PurchaserBorrowerName:
					ChicagoSelectors.GetPurchaseBorrowersNames(clients),
				PolicyType: GetPolicyType(fileType, allMortgages),
				ArePartiesSigningPowerOfAttorney:
					GetArePartiesSigningPowerOfAttorney(clients),
				FirmRepresentParty: GetFirmRepresentParty(
					fileType,
					mortgages,
					actingFor,
				),
				Mortgages: GetMortgages(
					mortgages,
					propertyDetails.ownerResidesAtProperty,
					closingDate,
				),
			};

			if (fileType === MatterFileType.PURCHASE && purchasePrice) {
				data.PurchasePrice = Number(centsToDollars(purchasePrice));
			}

			if (depositHolder) {
				data.WasDepositPaidToVendor = GetWasDepositPaidToVendor(depositHolder);
			}

			if (fileType === MatterFileType.REFINANCE) {
				data.ExistingUninsuredMortgages =
					GetExistingUninsuredMortgages(encumbrances);
			}

			const lawyerDetailString = GetLawyerDetails(
				fileType,
				actingFor,
				otherPartyLawFirm?.lawyer,
				mortgages,
			);

			if (lawyerDetailString) {
				data.LawyerDetail = lawyerDetailString;
			}

			if (
				propertyDetails.propertyType?.multiUnit &&
				propertyDetails.propertyType?.numberOfUnits
			) {
				data.NoOfResidentialUnit = propertyDetails.propertyType.numberOfUnits;
			}

			return data;
		} catch (err) {
			LogRocket.error('Error building Chicago Title Data Payload', err);
			return null;
		}
	}

	private static GetPurchaseBorrowersNames(
		clients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): PurchaserBorrowerName {
		const result: PurchaserBorrowerName = {
			Individuals: [],
			Corporates: [],
		};

		result.Individuals = clients
			.filter((x) => x.type === ClientType.PERSON)
			?.map((x: PersonMatterClientWithAssociations) => {
				const person: PurchaseBorrower = {
					FirstName: x.firstName,
					MiddleName: x.middleNames,
					LastName: x.lastName,
				};

				return person;
			});

		result.Corporates = clients
			.filter((x) => x.type !== ClientType.PERSON)
			.map((x: BusinessMatterClientWithAssociations) => x.name);

		return result;
	}

	@Selector([
		ChicagoSelectors.getChicagoDataPayload,
		SelectedMatter.getTitleInsurance,
	])
	static getPayloadHasChanged(
		curr: SubmitData,
		titleInsurance: MatterChicagoTitleInsurance,
	): boolean {
		let payloadHasChanged = true;
		if (titleInsurance?.provider !== TitleInsuranceProvider.CHICAGO) {
			return false;
		}

		if (!titleInsurance?.previousSubmittedPayload) {
			return false;
		}

		if (titleInsurance?.previousSubmittedPayload && !curr) {
			return true;
		}

		const previousPayload = cloneDeep(titleInsurance?.previousSubmittedPayload);
		const currentPayload = cloneDeep(curr);

		if (
			isEqual(
				removeUndefinedAndNull(previousPayload),
				removeUndefinedAndNull(currentPayload),
			)
		) {
			payloadHasChanged = false;
		}

		return payloadHasChanged;
	}

	// we need to make an address builder for chicago that build a street1/ street 2 correclty and takes into consideration the suite / units / po box
}
